import { useState, useCallback } from 'react';
import { PANEL_REGISTRY } from '../components/dashboard/panelRegistry';
import { StorageService } from '@/lib/StorageService';

const STORAGE_KEY = 'analytics_panels_v1';
const storage = new StorageService();

function buildDefaults(): Record<string, boolean> {
  return Object.fromEntries(PANEL_REGISTRY.map((p) => [p.id, p.defaultOn]));
}

function loadFromStorage(): Record<string, boolean> {
  try {
    const raw = storage.getPreference(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return {};
  }
}

function mergeWithDefaults(stored: Record<string, boolean>): Record<string, boolean> {
  const defaults = buildDefaults();
  return { ...defaults, ...stored };
}

function saveToStorage(state: Record<string, boolean>): void {
  try {
    storage.setPreference(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage may be unavailable
  }
}

type UsePanelStateReturn = {
  panelState: Record<string, boolean>;
  toggle: (id: string) => void;
  set: (id: string, value: boolean) => void;
};

export function usePanelState(): UsePanelStateReturn {
  const [panelState, setPanelState] = useState<Record<string, boolean>>(() => {
    const stored = loadFromStorage();
    return mergeWithDefaults(stored);
  });

  const toggle = useCallback((id: string) => {
    setPanelState((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveToStorage(next);
      return next;
    });
  }, []);

  const set = useCallback((id: string, value: boolean) => {
    setPanelState((prev) => {
      const next = { ...prev, [id]: value };
      saveToStorage(next);
      return next;
    });
  }, []);

  return { panelState, toggle, set };
}
